import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import ReactMarkdown from "react-markdown";
import {
  ArrowRight,
  ArrowLeft,
  Clock,
  Tag,
  WhatsappLogo,
  Calendar,
} from "@phosphor-icons/react";
import MagneticButton from "@/components/MagneticButton";
import Tilt3D from "@/components/Tilt3D";
import RunwayDivider from "@/components/RunwayDivider";
import SEOHead from "@/components/SEOHead";

const SITE_URL = "https://www.rijschool-autopilot.nl";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WHATSAPP_NUMBER = "31620933773";

// ============================================================
// Helpers
// ============================================================
const formatDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleDateString("nl-NL", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
};

// ============================================================
// Blog index
// ============================================================
export const BlogIndex = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let active = true;
    fetch(`${API}/blog?limit=30`)
      .then((r) => (r.ok ? r.json() : Promise.reject(r.statusText)))
      .then((data) => {
        if (active) {
          setPosts(data || []);
          setLoading(false);
        }
      })
      .catch((e) => {
        if (active) {
          setError(String(e));
          setLoading(false);
        }
      });
    return () => {
      active = false;
    };
  }, []);

  const featured = posts[0];
  const rest = posts.slice(1);

  return (
    <>
      <SEOHead
        title="Blog — Rijles tips, CBR examen & bijzondere verrichtingen | Rijschool AutoPilot"
        description="Wekelijkse artikelen over de rijprocedure, bijzondere verrichtingen, CBR-examen tips en nieuws. Geschreven voor leerlingen van Rijschool AutoPilot Eindhoven & Helmond."
        keywords="rijschool blog eindhoven, rijles tips, CBR examen, bijzondere verrichtingen, TTT tips, faalangst rijexamen, rotonde regels, snelweg rijden"
        lang="nl"
        canonical={`${SITE_URL}/blog`}
        ogLocale="nl_NL"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Blog",
          name: "Rijschool AutoPilot Blog",
          url: `${SITE_URL}/blog`,
          publisher: {
            "@type": "DrivingSchool",
            name: "Rijschool AutoPilot",
            url: SITE_URL,
          },
        }}
      />

      {/* Hero */}
      <section
        className="relative pt-40 pb-12 px-6 md:px-12 overflow-hidden noise"
        data-testid="blog-banner"
      >
        <div className="absolute inset-0 -z-0">
          <div className="absolute -top-32 -right-20 w-[520px] h-[520px] rounded-full bg-[rgba(212,175,55,0.08)] blur-3xl" />
        </div>
        <div className="relative max-w-7xl mx-auto">
          <div className="flex items-center gap-3 mb-4">
            <span className="runway-stripes w-16 block" />
            <span className="overline">Blog</span>
          </div>
          <h1
            className="text-5xl md:text-6xl lg:text-7xl tracking-tight font-light text-white leading-[1.05]"
            data-testid="blog-title"
          >
            Verhalen van
            <br />
            <span className="font-display-italic text-gold text-6xl md:text-7xl lg:text-8xl">
              achter het stuur.
            </span>
          </h1>
          <p className="text-lg text-silver mt-6 max-w-2xl leading-relaxed">
            Elke week nieuwe artikelen over de rijprocedure, bijzondere
            verrichtingen, CBR-examen tips, faalangst en alles wat je verder helpt
            op weg naar je rijbewijs. Persoonlijk geschreven, gericht op leerlingen
            in Eindhoven en Helmond.
          </p>
        </div>
      </section>

      <RunwayDivider />

      {/* Posts */}
      <section className="py-12 lg:py-16 px-6 md:px-12" data-testid="blog-list">
        <div className="max-w-7xl mx-auto">
          {loading && (
            <div className="text-silver text-center py-20">Artikelen laden…</div>
          )}
          {error && !loading && (
            <div className="text-silver text-center py-20">
              Kon artikelen niet laden. Probeer het later opnieuw.
            </div>
          )}
          {!loading && !error && posts.length === 0 && (
            <div className="text-silver text-center py-20">
              Er zijn nog geen artikelen. Kom snel terug — we publiceren wekelijks!
            </div>
          )}

          {/* Featured post */}
          {featured && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="mb-12"
            >
              <Link
                to={`/blog/${featured.slug}`}
                className="group relative block card-highlight scan-line overflow-hidden p-8 lg:p-12"
                data-testid="blog-featured"
              >
                <div className="flex items-center gap-2 mb-4 flex-wrap">
                  <span className="overline">{featured.category}</span>
                  <span className="text-silver/40">·</span>
                  <span className="text-xs text-silver/80 flex items-center gap-1.5">
                    <Calendar size={12} weight="duotone" />
                    {formatDate(featured.published_at)}
                  </span>
                  <span className="text-silver/40">·</span>
                  <span className="text-xs text-silver/80 flex items-center gap-1.5">
                    <Clock size={12} weight="duotone" /> {featured.reading_minutes} min lezen
                  </span>
                </div>
                <h2 className="text-3xl md:text-4xl lg:text-5xl tracking-tight font-light text-white leading-[1.1] mb-4 max-w-3xl">
                  {featured.title}
                </h2>
                <p className="text-silver leading-relaxed mb-6 max-w-2xl text-base lg:text-lg">
                  {featured.excerpt}
                </p>
                <span className="text-gold inline-flex items-center gap-2 text-sm font-medium tracking-wide">
                  Lees artikel
                  <ArrowRight size={16} weight="bold" className="anim-arrow" />
                </span>
              </Link>
            </motion.div>
          )}

          {/* Rest grid */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {rest.map((p, i) => (
              <motion.div
                key={p.id}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.15 }}
                transition={{ duration: 0.5, delay: i * 0.06 }}
              >
                <Tilt3D max={5} scale={1.01} className="h-full">
                  <Link
                    to={`/blog/${p.slug}`}
                    className="card p-7 h-full flex flex-col gap-3 group"
                    style={{ transformStyle: "preserve-3d" }}
                    data-testid={`blog-card-${p.slug}`}
                  >
                    <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-gold">
                      <span>{p.category}</span>
                    </div>
                    <h3 className="text-xl text-white tracking-tight leading-snug group-hover:text-gold transition-colors">
                      {p.title}
                    </h3>
                    <p className="text-sm text-silver leading-relaxed flex-1 truncate-3">
                      {p.excerpt}
                    </p>
                    <div className="flex items-center justify-between pt-3 border-t border-white/8 text-xs text-silver/70">
                      <span className="flex items-center gap-1.5">
                        <Calendar size={11} weight="duotone" />
                        {formatDate(p.published_at)}
                      </span>
                      <span className="flex items-center gap-1.5">
                        <Clock size={11} weight="duotone" /> {p.reading_minutes} min
                      </span>
                    </div>
                  </Link>
                </Tilt3D>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

// ============================================================
// Blog detail
// ============================================================
export const BlogDetail = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setPost(null);
    setError(null);
    fetch(`${API}/blog/${slug}`)
      .then((r) => {
        if (r.status === 404) return null;
        return r.ok ? r.json() : Promise.reject(r.statusText);
      })
      .then((data) => {
        if (!active) return;
        setPost(data);
        setLoading(false);
        // fetch related (latest 4 excluding current)
        return fetch(`${API}/blog?limit=4`).then((r) => r.json());
      })
      .then((data) => {
        if (active && data) {
          setRelated(data.filter((p) => p.slug !== slug).slice(0, 3));
        }
      })
      .catch((e) => {
        if (active) {
          setError(String(e));
          setLoading(false);
        }
      });
    window.scrollTo({ top: 0, behavior: "instant" });
    return () => {
      active = false;
    };
  }, [slug]);

  const waMsg = encodeURIComponent(
    "Hoi! Ik las jullie blog en wil graag een proefles plannen."
  );

  if (loading) {
    return (
      <div className="pt-40 pb-20 px-6 text-center text-silver">
        Artikel laden…
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="pt-40 pb-20 px-6 text-center" data-testid="blog-not-found">
        <h1 className="text-3xl text-white font-light mb-4">Artikel niet gevonden</h1>
        <p className="text-silver mb-8">Dit blog­bericht bestaat niet (meer).</p>
        <Link to="/blog" className="btn-ghost inline-flex">
          <ArrowLeft size={16} weight="bold" /> Naar de blog
        </Link>
      </div>
    );
  }

  return (
    <>
      <SEOHead
        title={`${post.title} | Rijschool AutoPilot Blog`}
        description={post.excerpt}
        keywords={post.tags?.join(", ")}
        lang="nl"
        canonical={`${SITE_URL}/blog/${post.slug}`}
        ogLocale="nl_NL"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "BlogPosting",
          headline: post.title,
          description: post.excerpt,
          datePublished: post.published_at,
          author: { "@type": "Organization", name: "Rijschool AutoPilot" },
          publisher: {
            "@type": "Organization",
            name: "Rijschool AutoPilot",
            url: SITE_URL,
          },
          mainEntityOfPage: `${SITE_URL}/blog/${post.slug}`,
          keywords: post.tags?.join(", "),
        }}
      />

      {/* Header */}
      <section
        className="relative pt-36 pb-10 px-6 md:px-12 overflow-hidden noise"
        data-testid="blog-detail-banner"
      >
        <div className="absolute inset-0 -z-0">
          <div className="absolute -top-32 -right-20 w-[520px] h-[520px] rounded-full bg-[rgba(212,175,55,0.06)] blur-3xl" />
        </div>
        <div className="relative max-w-3xl mx-auto">
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 text-sm text-silver/80 hover:text-gold transition-colors mb-8"
            data-testid="blog-back"
          >
            <ArrowLeft size={14} weight="bold" /> Terug naar de blog
          </Link>

          <div className="flex items-center gap-2 flex-wrap mb-4">
            <span className="overline">{post.category}</span>
            <span className="text-silver/40">·</span>
            <span className="text-xs text-silver/80 flex items-center gap-1.5">
              <Calendar size={12} weight="duotone" />
              {formatDate(post.published_at)}
            </span>
            <span className="text-silver/40">·</span>
            <span className="text-xs text-silver/80 flex items-center gap-1.5">
              <Clock size={12} weight="duotone" /> {post.reading_minutes} min lezen
            </span>
          </div>

          <h1
            className="text-4xl md:text-5xl lg:text-6xl tracking-tight font-light text-white leading-[1.1] mb-6"
            data-testid="blog-detail-title"
          >
            {post.title}
          </h1>

          <p className="text-lg text-silver leading-relaxed">{post.excerpt}</p>
        </div>
      </section>

      {/* Body */}
      <article className="py-10 px-6 md:px-12" data-testid="blog-detail-body">
        <div className="max-w-3xl mx-auto prose-autopilot">
          <ReactMarkdown
            components={{
              h2: ({ node, ...props }) => (
                <h2
                  className="text-2xl md:text-3xl text-white tracking-tight font-light mt-12 mb-4"
                  {...props}
                />
              ),
              h3: ({ node, ...props }) => (
                <h3
                  className="text-xl text-white tracking-tight mt-8 mb-3"
                  {...props}
                />
              ),
              p: ({ node, ...props }) => (
                <p
                  className="text-silver-light leading-relaxed mb-4 text-base lg:text-lg"
                  {...props}
                />
              ),
              ul: ({ node, ...props }) => (
                <ul
                  className="list-disc list-outside pl-5 space-y-2 mb-4 text-silver-light marker:text-gold"
                  {...props}
                />
              ),
              ol: ({ node, ...props }) => (
                <ol
                  className="list-decimal list-outside pl-5 space-y-2 mb-4 text-silver-light marker:text-gold"
                  {...props}
                />
              ),
              li: ({ node, ...props }) => (
                <li className="leading-relaxed" {...props} />
              ),
              strong: ({ node, ...props }) => (
                <strong className="text-white font-medium" {...props} />
              ),
              em: ({ node, ...props }) => (
                <em className="text-gold not-italic font-medium" {...props} />
              ),
              blockquote: ({ node, ...props }) => (
                <blockquote
                  className="border-l-2 border-gold/60 pl-5 my-6 text-silver italic"
                  {...props}
                />
              ),
              a: ({ node, ...props }) => (
                <a className="text-gold link-underline" {...props} />
              ),
            }}
          >
            {post.content}
          </ReactMarkdown>

          {/* Tags */}
          {post.tags?.length > 0 && (
            <div className="flex items-center gap-2 flex-wrap mt-12 pt-8 border-t border-white/10">
              <Tag size={14} weight="duotone" className="text-gold" />
              {post.tags.map((t) => (
                <span
                  key={t}
                  className="text-[11px] uppercase tracking-[0.18em] text-silver/80 border border-white/12 px-2.5 py-1"
                >
                  {t}
                </span>
              ))}
            </div>
          )}

          {/* CTA block */}
          <div
            className="mt-12 card-highlight scan-line p-8 lg:p-10 overflow-hidden"
            data-testid="blog-cta-block"
          >
            <div className="overline mb-3">Klaar om zelf te oefenen?</div>
            <h3 className="text-2xl md:text-3xl text-white font-light tracking-tight mb-3">
              Drive like a pilot —{" "}
              <span className="font-display-italic text-gold text-3xl md:text-4xl">
                start met een proefles.
              </span>
            </h3>
            <p className="text-silver leading-relaxed mb-6">
              Bij Rijschool AutoPilot in Eindhoven en Helmond brengen we deze theorie
              direct in de praktijk — rustig, persoonlijk en met écht inzicht.
            </p>
            <div className="flex flex-wrap items-center gap-3">
              <MagneticButton>
                <a
                  href={`https://wa.me/${WHATSAPP_NUMBER}?text=${waMsg}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-gold"
                  data-testid="blog-cta-wa"
                >
                  <WhatsappLogo size={18} weight="bold" /> Plan een proefles
                </a>
              </MagneticButton>
              <MagneticButton>
                <Link to="/pakketten" className="btn-ghost">
                  Bekijk pakketten
                </Link>
              </MagneticButton>
            </div>
          </div>
        </div>
      </article>

      {/* Related */}
      {related.length > 0 && (
        <section
          className="py-16 px-6 md:px-12 bg-secondary"
          data-testid="blog-related"
        >
          <div className="max-w-7xl mx-auto">
            <div className="overline mb-4">Lees ook</div>
            <h2 className="text-2xl md:text-3xl text-white tracking-tight font-light mb-8">
              Andere artikelen
            </h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {related.map((p) => (
                <Link
                  key={p.id}
                  to={`/blog/${p.slug}`}
                  className="card p-6 group"
                >
                  <div className="text-[10px] uppercase tracking-[0.22em] text-gold mb-2">
                    {p.category}
                  </div>
                  <div className="text-lg text-white tracking-tight leading-snug group-hover:text-gold transition-colors">
                    {p.title}
                  </div>
                  <div className="text-xs text-silver/70 mt-3 flex items-center gap-3">
                    <span className="flex items-center gap-1.5">
                      <Clock size={11} weight="duotone" /> {p.reading_minutes} min
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </>
  );
};
